import { valibotResolver } from "@hookform/resolvers/valibot";
import { PencilSimpleLine } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import { ExtensionURLSchema, type ExtensionURLSchemaType } from "../../schema";
import SettingsItemContainer from "../SettingsItemContainer";

export default function ExtensionURL() {
  const isDesktop = useTwBreakpoint("lg");
  const [isEditing, setIsEditing] = useState(false);
  const { agencySettings, isLoadingAgencySettings } = useAgencySettings();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { url: agencySettings ? agencySettings.extension_url : "" },
    resolver: valibotResolver(ExtensionURLSchema),
  });

  useEffect(() => {
    if (agencySettings) {
      reset({ url: agencySettings.extension_url });
    }
  }, [agencySettings]);

  function onSubmit({ url }: ExtensionURLSchemaType) {
    updateAgencySettings({
      updates: { extension_url: url, use_chrome_extension: !!url },
    }).then(() => {
      setIsEditing(false);
    });
  }
  function onCancel() {
    setIsEditing(false);
    reset({ url: agencySettings.extension_url });
  }

  return (
    <SettingsItemContainer className="items-center">
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <FormField
            label="Custom extension URL"
            htmlFor="extension-url"
            className="w-full"
            error={errors.url?.message}
            size={isDesktop ? "sm" : "lg"}
          >
            <div className="gap-x-2 gap-y-4 flex flex-col lg:flex-row">
              <Input
                id="extension-url"
                placeholder="Enter extension URL"
                variant={isDesktop ? "sm" : "lg"}
                className="lg:w-80"
                autoFocus
                {...register("url")}
              />
              <div className="flex gap-x-2 mt-auto ml-auto">
                <Button
                  variant="secondary-black"
                  size={isDesktop ? "sm" : "md"}
                  onClick={onCancel}
                  disabled={isUpdatingAgencySettings}
                >
                  Cancel
                </Button>
                <Button
                  size={isDesktop ? "sm" : "md"}
                  type="submit"
                  variant="secondary-purple"
                  isLoading={isUpdatingAgencySettings}
                >
                  Save
                </Button>
              </div>
            </div>
          </FormField>
        </form>
      ) : (
        <>
          <div className="flex flex-col gap-y-0.5">
            <span className="text-button-sm">Custom extension URL</span>
            {isLoadingAgencySettings ? (
              <Skeleton />
            ) : (
              <span className="text-black-500 text-xs-medium">
                {agencySettings.extension_url || "No extension URL set"}
              </span>
            )}
          </div>
          <Button
            variant="ghost-black"
            intent="iconOnly"
            className="ml-auto"
            onClick={() => setIsEditing(true)}
          >
            <PencilSimpleLine />
          </Button>
        </>
      )}
    </SettingsItemContainer>
  );
}
